import { Button } from '@crehana/compass.ui/v3/button';
import { DatePicker } from '@crehana/compass.ui/v3/date-picker';
import type { LANGUAGES } from '@crehana/ts-types';
import type { Locale } from 'date-fns';
import { differenceInCalendarDays, format } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import ptBR from 'date-fns/locale/pt-BR';
import { useTranslation } from '../i18n';

type TDateFilterBarProps = {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  appliedDateFrom: Date | undefined;
  appliedDateTo: Date | undefined;
  onDateFromChange: (date: Date | undefined) => void;
  onDateToChange: (date: Date | undefined) => void;
  onApplyFilter: () => void;
  disabled?: boolean;
  filterDisabled?: boolean;
};

const DATE_FNS_LOCALES: Record<LANGUAGES, Locale> = {
  es,
  en: enUS,
  pt: ptBR,
};

function formatDate(date: Date, locale: Locale): string {
  return format(date, 'EEE d MMM yyyy', { locale })
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatRangeInfo({
  languageCode,
  t,
  from,
  to,
}: {
  languageCode: LANGUAGES;
  t: ReturnType<typeof useTranslation>['t'];
  from: Date | undefined;
  to: Date | undefined;
}): string {
  if (!from || !to) {
    return '';
  }

  const locale = DATE_FNS_LOCALES[languageCode];
  const diffDays = differenceInCalendarDays(to, from) + 1;

  return `${formatDate(from, locale)} - ${formatDate(to, locale)} ${t(
    'FILTER_RANGE_DAYS',
    {
      interpolation: { count: diffDays },
    },
  )}`;
}

export const DateFilterBar = ({
  dateFrom,
  dateTo,
  appliedDateFrom,
  appliedDateTo,
  onDateFromChange,
  onDateToChange,
  onApplyFilter,
  disabled = false,
  filterDisabled = false,
}: TDateFilterBarProps) => {
  const { t, languageCode } = useTranslation();

  return (
    <div
      className="tw-flex tw-items-center tw-gap-1.6 tw-justify-between tw-cds-bg-primary tw-cds-rounded-s tw-py-1.2 tw-px-2.4"
      style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      <div className="tw-flex tw-items-center tw-gap-1.2">
        <span className="tw-cds-font-subtitle-01">
          {t('FILTER_PERIOD_LABEL')}
        </span>

        <DatePicker
          mode="single"
          selected={dateFrom}
          onSelect={onDateFromChange}
          disabled={disabled}
          className="tw-w-14.4"
        />

        <span className="tw-cds-font-subtitle-01">
          {t('FILTER_DATE_SEPARATOR')}
        </span>

        <DatePicker
          mode="single"
          selected={dateTo}
          onSelect={onDateToChange}
          disabled={disabled}
          className="tw-w-14.4"
        />

        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onApplyFilter}
          disabled={disabled || filterDisabled}
        >
          {t('FILTER_BUTTON')}
        </Button>
      </div>

      <span className="tw-cds-font-body-01 tw-cds-text-subtle tw-shrink-0">
        {formatRangeInfo({
          languageCode,
          t,
          from: appliedDateFrom,
          to: appliedDateTo,
        })}
      </span>
    </div>
  );
};
